import { buttonVariants } from "components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ComponentProps } from "react";
import { DayPicker } from "react-day-picker";
import { useCurrentLanguage } from "utils/language";
import { cn } from "utils/ui";

export type CalendarProps = ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const { locale } = useCurrentLanguage();

  return (
    <DayPicker
      locale={locale}
      showOutsideDays={showOutsideDays}
      navLayout="around"
      className={cn("w-fit p-2", className)}
      classNames={{
        months: "flex w-fit flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        // prev | month label | next on row 1; day grid on row 2
        month: "grid w-fit grid-cols-[auto_1fr_auto] items-center gap-x-1 gap-y-3",
        month_caption: "col-start-2 row-start-1 flex items-center justify-center px-2",
        caption_label: "text-sm font-medium",
        nav: "hidden",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "col-start-1 row-start-1 size-7 bg-transparent p-0 opacity-70 hover:opacity-100 aria-disabled:pointer-events-none aria-disabled:invisible"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "col-start-3 row-start-1 size-7 bg-transparent p-0 opacity-70 hover:opacity-100 aria-disabled:pointer-events-none aria-disabled:invisible"
        ),
        month_grid: "col-span-3 row-start-2 w-fit border-collapse space-y-1",
        weekday: "text-slate-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-slate-400",
        week: "mt-1",
        day: "relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:bg-ribbon-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md dark:[&:has([aria-selected])]:bg-ribbon-500/40",
        day_button: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        range_start: "day-range-start rounded-l-md",
        range_end: "day-range-end rounded-r-md",
        selected:
          "bg-ribbon-500 text-white rounded hover:bg-ribbon-500 hover:text-white focus:bg-ribbon-500 focus:text-white dark:bg-ribbon-500 dark:text-white dark:hover:bg-ribbon-500 dark:hover:text-white dark:focus:bg-ribbon-500 dark:focus:text-white",
        today: "text-ribbon-500 dark:text-ribbon-400",
        outside: "text-slate-500 opacity-50 dark:text-slate-400",
        disabled: "text-slate-500 opacity-50 dark:text-slate-400",
        range_middle:
          "rounded-none aria-selected:bg-ribbon-100 aria-selected:text-ribbon-900 dark:aria-selected:bg-ribbon-500/40 dark:aria-selected:text-slate-50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: (chevronProps) => {
          if (chevronProps.orientation === "left") {
            return <ChevronLeft className="size-4" {...chevronProps} />;
          }
          return <ChevronRight className="size-4" {...chevronProps} />;
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
